import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { PERMISSIONS, type Permission } from '@/core/auth/permissions.js';
import { LoginPage, useRestoreSession, useSession } from '@/features/auth';
import { LoadingState } from '@/shared/ui/index.js';

import { AppLayout } from './app-layout.js';
import { RequireAuth, RequirePermission } from './route-guards.js';

/**
 * Cada sección se carga bajo demanda: quien solo registra el menú del día no
 * descarga el código de reportes ni de usuarios.
 */
const DashboardPage = lazy(async () => ({
  default: (await import('@/features/dashboard')).DashboardPage,
}));
const KitchenPage = lazy(async () => ({
  default: (await import('@/features/dashboard')).KitchenPage,
}));
const ProductsPage = lazy(async () => ({
  default: (await import('@/features/products')).ProductsPage,
}));
const InventoryPage = lazy(async () => ({
  default: (await import('@/features/inventory')).InventoryPage,
}));
const AlertsPage = lazy(async () => ({
  default: (await import('@/features/inventory')).AlertsPage,
}));
const KardexPage = lazy(async () => ({
  default: (await import('@/features/kardex')).KardexPage,
}));
const AuditPage = lazy(async () => ({
  default: (await import('@/features/audit')).AuditPage,
}));
const ReportsPage = lazy(async () => ({
  default: (await import('@/features/reports')).ReportsPage,
}));
const UsersPage = lazy(async () => ({
  default: (await import('@/features/users')).UsersPage,
}));
const ProfilePage = lazy(async () => ({
  default: (await import('@/features/users')).ProfilePage,
}));
const DailyMenuPage = lazy(async () => ({
  default: (await import('@/features/daily-menu')).DailyMenuPage,
}));

interface ProtectedRoute {
  readonly path: string;
  readonly permission: Permission;
  readonly element: React.ReactNode;
}

/** Una sola tabla de rutas: agregar una sección es una línea. */
const ROUTES: readonly ProtectedRoute[] = [
  { path: 'insumos', permission: PERMISSIONS.productsRead, element: <ProductsPage /> },
  { path: 'inventario', permission: PERMISSIONS.productsRead, element: <InventoryPage /> },
  { path: 'menu-del-dia', permission: PERMISSIONS.inventoryWrite, element: <DailyMenuPage /> },
  { path: 'kardex', permission: PERMISSIONS.reportsRead, element: <KardexPage /> },
  { path: 'historial', permission: PERMISSIONS.reportsRead, element: <AuditPage /> },
  { path: 'alertas', permission: PERMISSIONS.reportsRead, element: <AlertsPage /> },
  { path: 'reportes', permission: PERMISSIONS.reportsRead, element: <ReportsPage /> },
  { path: 'usuarios', permission: PERMISSIONS.usersManage, element: <UsersPage /> },
];

/** El inicio depende del rol: panel gerencial o tablero de operación. */
function HomePage() {
  const { can } = useSession();
  return can(PERMISSIONS.reportsRead) ? <DashboardPage /> : <KitchenPage />;
}

export function AppRouter() {
  useRestoreSession();
  const { status } = useSession();

  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        <Route
          element={status === 'autenticada' ? <Navigate replace to="/" /> : <LoginPage />}
          path="/ingresar"
        />
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
          path="/"
        >
          {/* Cocina no tiene acceso a reportes: su inicio es otro, no un cartel
              de "sin permiso" sobre un panel que nunca va a poder abrir. */}
          <Route element={<HomePage />} index />
          {ROUTES.map((route) => (
            <Route
              element={
                <RequirePermission permission={route.permission}>
                  {route.element}
                </RequirePermission>
              }
              key={route.path}
              path={route.path}
            />
          ))}
          {/* El perfil no exige permiso: toda sesión puede ver su propia cuenta. */}
          <Route element={<ProfilePage />} path="perfil" />
        </Route>
        <Route element={<Navigate replace to="/" />} path="*" />
      </Routes>
    </Suspense>
  );
}
